// src/utils/api.js
// Network-resilient API layer:
//   - Mutating requests get an `Idempotency-Key` and are queued in IndexedDB
//     when the network fails. Replay is in-order with exponential backoff,
//     bounded by attempts (5) and a 12h TTL (see offline-cache.js).
//   - GETs to cacheable routes (menu, event detail, etc.) are mirrored to an
//     IndexedDB cache. On offline-failure the cache is the fallback.
//   - 402 PAYMENT_REQUIRED / PLAN_LIMIT_REACHED responses dispatch a billing
//     event so the UpgradePrompt / PaymentGate components can react.
// See docs/system/offline-mode.md for the full rationale.

import axios from 'axios';
import { dispatchBillingEvent } from './billingEvents';
import { getApiBaseUrl, isNative } from '../config/runtime';
import {
	cacheGet,
	cacheKeyForUrl,
	cacheSet,
	isCacheable,
	makeIdempotencyKey,
	queueAdd,
	queueAll,
	queueCount,
	queuePurgeExpired,
	queueRemove,
	queueUpdate,
} from '../services/offline-cache';

const AUTH_TOKEN_KEY = 'token';
const AUTH_USER_KEY = 'user';
const GUEST_TOKEN_KEY = 'guestSessionToken';
const GUEST_NAME_KEY = 'guestDisplayName';
// Auth endpoints must never be queued — replaying a stale login/register
// silently after a network blip would surprise the user.
const SENSITIVE_REQUEST_PATTERNS = [/\/auth\/login(?:$|\?)/, /\/auth\/register(?:$|\?)/];

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);
const MAX_QUEUE_ATTEMPTS = 5;

let isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
let queueLength = 0;
let listeners = [];
let processing = false;
let processingTimer = null;

function notifyAuthStateChanged() {
	if (typeof window === 'undefined') return;
	window.dispatchEvent(new CustomEvent('eventory:auth-state-changed'));
}

// Create axios instance.
// baseURL is '/api' (same-origin) on web, or the absolute Heroku origin
// when running inside the native Capacitor shell — see config/runtime.js.
// Native networks (mobile data) are slower than office WiFi, so we give
// the native build a longer timeout.
const api = axios.create({
	baseURL: getApiBaseUrl(),
	timeout: isNative() ? 20000 : 10000,
});

// Include token in headers if available, and tag every mutating request with
// an idempotency key (unless one is already set by the caller — e.g. a queue
// replay reusing the original key).
api.interceptors.request.use((config) => {
	const token = getAuthToken();
	if (token) {
		config.headers['x-auth-token'] = token;
	}
	const method = (config.method || 'get').toLowerCase();
	if (MUTATING_METHODS.has(method)) {
		if (!config.headers['Idempotency-Key']) {
			config.headers['Idempotency-Key'] = makeIdempotencyKey();
		}
	}
	return config;
});

// Response interceptor: cache cacheable GETs, queue mutations on network error,
// surface 402 billing codes via dispatchBillingEvent.
api.interceptors.response.use(
	(response) => {
		const config = response.config || {};
		const method = (config.method || 'get').toLowerCase();
		if (method === 'get' && isCacheable(config.url)) {
			const key = cacheKeyForUrl(config.url);
			if (key) cacheSet(key, response.data);
		}
		return response;
	},
	async (error) => {
		const originalRequest = error.config || {};
		const method = (originalRequest.method || 'get').toLowerCase();
		const billingCode = error.response?.data?.code;

		if (error.response?.status === 402 && billingCode) {
			dispatchBillingEvent({
				type: billingCode === 'PAYMENT_REQUIRED' ? 'payment_required' : 'plan_limit_reached',
				...error.response.data,
			});
		}

		// 4xx — surface to the caller, never retry (it's a client bug or auth issue).
		if (error.response && error.response.status >= 400 && error.response.status < 500) {
			return Promise.reject(error);
		}

		// GETs: try the IDB cache as a fallback before rejecting.
		if (method === 'get' && isCacheable(originalRequest.url)) {
			const key = cacheKeyForUrl(originalRequest.url);
			if (key) {
				const cached = await cacheGet(key);
				if (cached) {
					return {
						data: cached.value,
						status: 200,
						statusText: 'OK (cache)',
						headers: {},
						config: originalRequest,
						fromCache: true,
					};
				}
			}
			return Promise.reject(error);
		}

		// Mutations on network error or 5xx: queue for replay (skip sensitive
		// auth requests and payment requests where stale replays would be unsafe).
		const isNetworkFailure = !error.response;
		const isServerError = error.response && error.response.status >= 500;
		if (
			MUTATING_METHODS.has(method) &&
			(isNetworkFailure || isServerError) &&
			// A failed *replay* must not re-enqueue itself — replayEntry already
			// owns that entry's retry/backoff. Without this guard a still-offline
			// replay duplicates the entry on every attempt (same Idempotency-Key,
			// so the server dedupes, but the queue balloons during a long outage).
			!originalRequest.__queueReplay &&
			shouldQueueRequest(originalRequest) &&
			!String(originalRequest.url || '').includes('/payments/')
		) {
			const idempotencyKey =
				(originalRequest.headers && originalRequest.headers['Idempotency-Key']) ||
				makeIdempotencyKey();
			const headersToPersist = pickPersistableHeaders(originalRequest.headers);
			const entry = await queueAdd({
				url: originalRequest.url,
				method,
				data: originalRequest.data ? safeParseRequestBody(originalRequest.data) : null,
				headers: { ...headersToPersist, 'Idempotency-Key': idempotencyKey },
				idempotencyKey,
			});
			await refreshQueueLength();
			scheduleProcess(500);
			// Resolve with a "queued" pseudo-response so the caller can update UI
			// optimistically. The actual server result will not be observable.
			return {
				data: { queued: true, idempotencyKey, id: entry.id },
				status: 202,
				statusText: 'Queued',
				headers: {},
				config: originalRequest,
				queued: true,
			};
		}

		return Promise.reject(error);
	}
);

function shouldQueueRequest(request) {
	// FormData bodies (file uploads) can't be serialized into IndexedDB — a
	// queued upload would replay as an empty/broken request forever. Fail fast
	// so the user sees the error and retries with the file still selected.
	if (typeof FormData !== 'undefined' && request?.data instanceof FormData) return false;
	const normalizedUrl = String(request?.url || '').toLowerCase();
	return !SENSITIVE_REQUEST_PATTERNS.some((pattern) => pattern.test(normalizedUrl));
}

// ── Queue processing ─────────────────────────────────────────────────────────

function scheduleProcess(delayMs = 0) {
	if (processingTimer) return;
	processingTimer = setTimeout(() => {
		processingTimer = null;
		processQueue();
	}, delayMs);
}

async function processQueue() {
	if (processing) return;
	if (typeof navigator !== 'undefined' && !navigator.onLine) return;
	processing = true;
	try {
		const dropped = await queuePurgeExpired();
		if (dropped > 0) console.info(`offline-queue: dropped ${dropped} expired entries`);

		// Loop one-at-a-time so order is preserved.
		// eslint-disable-next-line no-constant-condition
		while (true) {
			if (typeof navigator !== 'undefined' && !navigator.onLine) break;
			const queue = await queueAll();
			if (queue.length === 0) break;
			const head = queue[0];
			const succeeded = await replayEntry(head);
			if (!succeeded) {
				const attempt = (head.attempts || 0) + 1;
				const delay = Math.min(60000, 2 ** attempt * 1000);
				scheduleProcess(delay);
				break;
			}
		}
	} finally {
		processing = false;
		await refreshQueueLength();
	}
}

async function replayEntry(entry) {
	try {
		const attempts = (entry.attempts || 0) + 1;
		await queueUpdate(entry.id, { attempts });

		await api.request({
			url: entry.url,
			method: entry.method,
			data: entry.data,
			headers: entry.headers || {},
			__queueReplay: true,
		});
		await queueRemove(entry.id);
		return true;
	} catch (err) {
		const isPermanentClientError =
			err && err.response && err.response.status >= 400 && err.response.status < 500;
		const attempts = (entry.attempts || 0) + 1;
		const exhausted = attempts >= MAX_QUEUE_ATTEMPTS;
		if (isPermanentClientError || exhausted) {
			console.warn(
				`offline-queue: dropping entry ${entry.id} after ${attempts} attempt(s)`,
				err && err.response ? err.response.status : err
			);
			await queueRemove(entry.id);
			return true;
		}
		await queueUpdate(entry.id, {
			lastError: (err && err.message) || 'Unknown error',
		});
		return false;
	}
}

async function refreshQueueLength() {
	const next = await queueCount();
	if (next !== queueLength) {
		queueLength = next;
		notifyListeners();
	}
}

function pickPersistableHeaders(headers) {
	if (!headers || typeof headers !== 'object') return {};
	const allowList = ['x-guest-token', 'content-type'];
	const out = {};
	for (const key of Object.keys(headers)) {
		const lower = key.toLowerCase();
		if (allowList.includes(lower)) out[key] = headers[key];
	}
	return out;
}

function safeParseRequestBody(data) {
	if (data == null) return null;
	if (typeof data === 'string') {
		try {
			return JSON.parse(data);
		} catch (err) {
			return data;
		}
	}
	return data;
}

// ── Online/offline detection ─────────────────────────────────────────────────

function updateOnlineStatus() {
	const wasOffline = !isOnline;
	isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
	notifyListeners();
	if (wasOffline && isOnline) {
		scheduleProcess(100);
	}
}

if (typeof window !== 'undefined') {
	window.addEventListener('online', updateOnlineStatus);
	window.addEventListener('offline', updateOnlineStatus);
}

// ── Public API ───────────────────────────────────────────────────────────────

function getConnectivityMode() {
	if (!isOnline) return 'queueing';
	if (queueLength > 0) return 'cloud-degraded';
	return 'online';
}

function subscribe(listener) {
	listeners.push(listener);
	return () => {
		listeners = listeners.filter((l) => l !== listener);
	};
}

function notifyListeners() {
	const snapshot = getNetworkStatus();
	listeners.forEach((listener) => {
		try {
			listener(snapshot);
		} catch (err) {
			// One bad listener shouldn't break the others.
		}
	});
}

function getNetworkStatus() {
	return {
		isOnline,
		queueLength,
		connectivityMode: getConnectivityMode(),
	};
}

function getBrowserStorage(type = 'session') {
	if (typeof window === 'undefined') return null;
	try {
		return type === 'local' ? window.localStorage : window.sessionStorage;
	} catch (e) {
		return null;
	}
}

function getStoredValue(key, storageType = 'session') {
	const storage = getBrowserStorage(storageType);
	if (!storage) return null;
	try {
		return storage.getItem(key);
	} catch (e) {
		return null;
	}
}

function setStoredValue(key, value, storageType = 'session') {
	const storage = getBrowserStorage(storageType);
	if (!storage) return;
	try {
		if (value === null || value === undefined || value === '') {
			storage.removeItem(key);
		} else {
			storage.setItem(key, String(value));
		}
	} catch (e) {
		// Ignore storage errors.
	}
}

function getAuthToken() {
	return getStoredValue(AUTH_TOKEN_KEY, 'local');
}

function setAuthToken(token) {
	setStoredValue(AUTH_TOKEN_KEY, token, 'local');
}

function clearAuthToken() {
	setStoredValue(AUTH_TOKEN_KEY, null, 'local');
}

function getStoredUser() {
	const rawUser = getStoredValue(AUTH_USER_KEY, 'local');
	if (!rawUser) return null;
	try {
		return JSON.parse(rawUser);
	} catch (e) {
		clearStoredUser();
		return null;
	}
}

function setStoredUser(user) {
	if (!user) {
		clearStoredUser();
		return;
	}
	try {
		setStoredValue(AUTH_USER_KEY, JSON.stringify(user), 'local');
	} catch (e) {
		// Ignore storage errors.
	}
}

function clearStoredUser() {
	setStoredValue(AUTH_USER_KEY, null, 'local');
}

function getGuestSessionToken() {
	return getStoredValue(GUEST_TOKEN_KEY);
}

function setGuestSessionToken(token) {
	setStoredValue(GUEST_TOKEN_KEY, token);
	notifyAuthStateChanged();
}

function clearGuestSessionToken() {
	setStoredValue(GUEST_TOKEN_KEY, null);
	notifyAuthStateChanged();
}

function getGuestDisplayName() {
	return getStoredValue(GUEST_NAME_KEY) || '';
}

function setGuestDisplayName(name) {
	setStoredValue(GUEST_NAME_KEY, name);
}

function getGuestHeaders(existingHeaders = {}) {
	const guestToken = getGuestSessionToken();
	if (!guestToken) return existingHeaders;
	return {
		...existingHeaders,
		'x-guest-token': guestToken,
	};
}

// Restore on load: kick a queue scan so pending work from a prior session
// makes progress as soon as the page mounts.
if (typeof window !== 'undefined') {
	refreshQueueLength()
		.then(() => {
			if (queueLength > 0) {
				console.info(`offline-queue: ${queueLength} pending request(s) restored from storage`);
				scheduleProcess(1000);
			}
		})
		.catch(() => {
			// Ignore — IDB might be unavailable.
		});
}

export { subscribe, getNetworkStatus, processQueue, getConnectivityMode };
export {
	getAuthToken,
	setAuthToken,
	clearAuthToken,
	getStoredUser,
	setStoredUser,
	clearStoredUser,
	getGuestSessionToken,
	setGuestSessionToken,
	clearGuestSessionToken,
	getGuestDisplayName,
	setGuestDisplayName,
	getGuestHeaders,
};
export default api;
